import { useCallback, useEffect, useRef } from 'react'
import { TableInstance } from 'react-table'
import useGetTableFilters from 'core/table/table-hooks/useGetTableFilters'
import useGetTableSortBy from 'core/table/table-hooks/useGetTableSortBy'
import useSetTableFilters from 'core/table/table-hooks/useSetTableFilters'
import useSetTableSortBy from 'core/table/table-hooks/useSetTableSortBy'
import {
  filterRowsToSaved,
  savedFiltersToFilterRows,
  sortByToSortOrder,
  sortOrderToSortBy,
} from 'core/table/interfaces/TablePersistence'
import { FilterRow } from 'core/table/interfaces/Filters'
import useDebounce from 'utils/useDebounce'

type UseTablePersistenceArgs = {
  tableName?: string
  table: TableInstance
  enabled: boolean
  hasUrlFilters: boolean
  onPersistedFiltersLoaded?: (rows: FilterRow[]) => void
  persistSort?: boolean
  serverSide?: boolean
}

export const useTablePersistence = ({
  tableName,
  table,
  enabled,
  hasUrlFilters,
  onPersistedFiltersLoaded,
  persistSort = true,
  serverSide = false,
}: UseTablePersistenceArgs) => {
  const [fetchTableFilters, { result: savedFilters, loading: filtersLoading, called: filtersCalled }] =
    useGetTableFilters()
  const [fetchTableSortBy, { result: savedSortBy, loading: sortLoading, called: sortCalled }] = useGetTableSortBy()
  const [setTableFilters] = useSetTableFilters()
  const [setTableSortBy] = useSetTableSortBy()
  const preferencesLoaded = useRef(false)
  const skipSortSave = useRef(true)
  const tableSortBy = (table.state as { sortBy?: { id: string; desc?: boolean }[] }).sortBy || []
  const debouncedSortBy = useDebounce(tableSortBy, 300)

  useEffect(() => {
    if (!enabled || !tableName) {
      return
    }

    fetchTableFilters(tableName)
    if (persistSort) {
      fetchTableSortBy(tableName)
    }
  }, [enabled, fetchTableFilters, fetchTableSortBy, persistSort, tableName])

  useEffect(() => {
    if (!enabled || !tableName) {
      return
    }

    const filtersReady = filtersCalled && !filtersLoading
    const sortReady = !persistSort || (sortCalled && !sortLoading)

    if (!filtersReady || !sortReady) {
      return
    }

    if (hasUrlFilters) {
      skipSortSave.current = false
      return
    }

    if (preferencesLoaded.current) {
      return
    }

    preferencesLoaded.current = true

    if (savedFilters?.length) {
      const rows = savedFiltersToFilterRows(savedFilters)
      if (!serverSide) {
        // @ts-ignore
        table.setAllFilters(rows.map(({ column, value }) => ({ id: column, value })))
      }
      onPersistedFiltersLoaded?.(rows)
    }

    const sortBy = persistSort ? sortOrderToSortBy(savedSortBy?.sortOrder || '') : []
    if (sortBy.length) {
      // @ts-ignore
      table.setSortBy(sortBy)
    }

    skipSortSave.current = false
  }, [
    enabled,
    filtersCalled,
    filtersLoading,
    hasUrlFilters,
    onPersistedFiltersLoaded,
    persistSort,
    serverSide,
    savedFilters,
    savedSortBy,
    sortCalled,
    sortLoading,
    table,
    tableName,
  ])

  const persistFilters = useCallback(
    (rows: FilterRow[]) => {
      if (!tableName) {
        return
      }

      setTableFilters(tableName, filterRowsToSaved(rows))
    },
    [setTableFilters, tableName],
  )

  useEffect(() => {
    if (!enabled || !tableName || !persistSort || skipSortSave.current) {
      return
    }

    const sortOrder = sortByToSortOrder(debouncedSortBy)
    if (!sortOrder) {
      return
    }

    setTableSortBy(tableName, sortOrder)
  }, [debouncedSortBy, enabled, persistSort, setTableSortBy, tableName])

  return {
    persistFilters,
  }
}

export default useTablePersistence
